import {FormCommonProps} from "../../../common/forms/types.ts";
import {AsyncFetchRender} from "../../../common/async/AsyncFetchRender.tsx";
import {Reclamation} from "src/api/generated/erp/db/types/tables/reclamation.ts";
import {DeepPartial, useForm} from "react-hook-form";
import {AavoForm} from "../../../common/forms/AavoForm.tsx";
import {FormTextField} from "../../../common/forms/fields/FormTextField.tsx";
import i18n from "i18next";
import {requireRule} from "../../../common/forms/validation.ts";
import {FormSelectField} from "../../../common/forms/fields/FormSelectField.tsx";
import {FormDateField} from "../../../common/forms/fields/FormDateField.tsx";
import {Divider} from "@mui/material";
import {currentLocalDateAsString} from "src/utils/dayjsUtils.ts";
import {UserApi} from "src/api/generated/users/api/userApi.ts";
import {
    ReclamationFormApi,
    ReclamationFormApi_InitData,
} from "src/api/generated/erp/reclamations/api/reclamationFormApi.ts";
import {FormAsyncSelectField} from "../../../common/forms/fields/FormAsyncSelectField.tsx";
import {AppUser} from "src/api/generated/postgres/db/types/role_management/tables/appUser.ts";
import {getCountryLabels} from "src/api/generated/erp/db/types/enums/country.ts";
import {enumOptions} from "src/components/common/forms/fields/selectionFieldUtils.ts";

export interface ReclamationFormProps extends FormCommonProps<number> {
	reclamationId: number | null | undefined;
	newReclamationDefaultValues?: DeepPartial<Reclamation>;
}

export const ReclamationForm = (props: ReclamationFormProps) => {
	const { reclamationId } = props;
	return (
		<AsyncFetchRender
			fetch={() => ReclamationFormApi.getInitData({ reclamationId })}
			content={(initData) => <ReclamationFormContent initData={initData} {...props} />}
		/>
	);
};

interface ReclamationFormContentProps extends ReclamationFormProps {
	initData: ReclamationFormApi_InitData;
}

const ReclamationFormContent = ({
	initData,
	onFormEdited,
	newReclamationDefaultValues,
	...other
}: ReclamationFormContentProps) => {
	const {
		control,
		handleSubmit,
		formState: { isDirty },
		watch,
	} = useForm<Reclamation>({
		mode: "all",
		defaultValues: initData.reclamation ?? {
			plannedCompletedDate: currentLocalDateAsString(),
			categoryId: initData.reclamationCategories[0]?.reclamationCategoryId ?? undefined,
			causeId: initData.reclamationCauses[0]?.reclamationCauseId ?? undefined,
			address: {
				country: "FINLAND",
			},
			...newReclamationDefaultValues,
		},
	});
	if (onFormEdited) watch(onFormEdited);

	return (
		<AavoForm
			handleSubmit={handleSubmit}
			submit={submitForm}
			isDirty={isDirty}
			columns={3}
			{...other}
		>
			<FormTextField
				control={control}
				name={"title"}
				label={i18n.t("title")}
				rules={requireRule()}
			/>
			<FormAsyncSelectField<Reclamation, AppUser, number>
				control={control}
				name={"responsiblePersonUserId"}
				label={i18n.t("responsible_person")}
				fetchOptions={async ({ searchQuery, currentSelection }) =>
					UserApi.getUserSelectionOptions({
						currentSelectionUserId: currentSelection,
						searchQuery: searchQuery,
					})
				}
				getOptionKey={(option) => option.id}
				getOptionLabel={(option) => option.name}
				rules={requireRule()}
			/>
			<FormTextField control={control} name={"subjectCode"} label={i18n.t("subjectCode")} />
			<FormSelectField
				control={control}
				name={"categoryId"}
				label={i18n.t("category")}
				options={initData.reclamationCategories.map((category) => ({
					key: category.reclamationCategoryId,
					label: category.name,
				}))}
				rules={requireRule()}
			/>
			<FormSelectField
				control={control}
				name={"causeId"}
				label={i18n.t("cause")}
				options={initData.reclamationCauses.map((cause) => ({
					key: cause.reclamationCauseId,
					label: cause.name,
				}))}
				rules={requireRule()}
			/>
			<FormDateField
				control={control}
				name={"plannedCompletedDate"}
				label={i18n.t("planned_completed_date")}
				rules={requireRule()}
			/>
			<FormTextField
				control={control}
				name={"description"}
				label={i18n.t("description")}
				multiline={true}
				minRows={2}
				sx={{
					gridColumn: "1 / -1",
				}}
			/>
			<FormTextField
				control={control}
				name={"demand"}
				label={i18n.t("demand")}
				multiline={true}
				minRows={2}
				sx={{
					gridColumn: "1 / -1",
				}}
			/>
			<FormTextField
				control={control}
				name={"act"}
				label={i18n.t("act")}
				multiline={true}
				minRows={2}
				sx={{
					gridColumn: "1 / -1",
				}}
			/>
			<Divider
				textAlign={"left"}
				sx={{
					gridColumn: "1 / -1",
				}}
			>
				{i18n.t("complainer")}
			</Divider>
			<FormTextField control={control} name={"complainerName"} label={i18n.t("complainer")} />
			<FormTextField
				control={control}
				name={"complainerEmail"}
				label={i18n.t("complainer_email")}
			/>
			<FormTextField
				control={control}
				name={"complainerPhone"}
				label={i18n.t("complainer_phone")}
			/>
			<Divider
				textAlign={"left"}
				sx={{
					gridColumn: "1 / -1",
				}}
			>
				{i18n.t("address")}
			</Divider>
			<FormTextField
				control={control}
				name={"address.address_1"}
				label={i18n.t("street_address")}
			/>
			<FormTextField
				control={control}
				name={"address.address_2"}
				label={i18n.t("address_2")}
			/>
			<FormTextField control={control} name={"address.city"} label={i18n.t("city")} />
			<FormTextField
				control={control}
				name={"address.postalCode"}
				label={i18n.t("postal_code")}
			/>
			<FormSelectField
				control={control}
				name={"address.country"}
				label={i18n.t("country")}
				options={enumOptions(getCountryLabels())}
			/>
		</AavoForm>
	);
};

const submitForm = async (reclamation: Reclamation): Promise<number> => {
	return await ReclamationFormApi.save({
		reclamation: reclamation,
	});
};
